import { twyneChainData, twyneConfigFor } from '@1delta/data-sdk'
import {
  TwyneVaultManagerAbi,
  TwyneCollateralVaultFactoryAbi,
  TwyneATokenWrapperAbi,
  AaveV2V3Abi,
} from '@1delta/abis'
import { multicallRetryUniversal } from '@1delta/providers'
import type { TwyneMarketRaw, TwyneMarketsRaw } from './types'

/**
 * Fetch every Twyne market on a chain — FULLY ON-CHAIN, no API.
 *
 * The roster is NOT discovered here. `VaultManager` keeps its whitelists as
 * un-enumerable mappings and governance adds markets by multisig, so the
 * complete set only exists in the event log; the lender-metadata generator
 * replays it and this fetcher reads the CURATED result and fails closed on
 * anything absent. (`fetchLenderAll` still routes Twyne through the external
 * slot — the generic on-chain path has no discovery branch for it and would
 * return an empty map.)
 *
 * Four things this reads that a naive port would get wrong:
 *
 *  1. **The external liquidation threshold is eMode-dependent.** Every live
 *     Aave-side Twyne market sits in an eMode category (1 / 39 / 45 / 48), so
 *     the threshold comes from `getEModeCategoryCollateralConfig(categoryId)`,
 *     NOT from the reserve's own configuration. Reading the plain reserve LT
 *     silently understates the whole LTV band.
 *  2. **It is the LOWER bound of a BAND, and it moves.** A position's LTV is
 *     user-chosen between `externalLiqLtv * buffer` and `maxTwyneLTVs`; Aave
 *     ramps PT eMode thresholds down as a PT ages, so this can never be cached.
 *  3. **The market's borrow rate is the EXTERNAL one.** The credit vault's rate
 *     is charged on RESERVED CREDIT in the COLLATERAL asset — a different base
 *     entirely — so the two must never be summed into one market rate.
 *  4. **Maturity is computed at read time.** Two of the three live PT markets
 *     are already matured; a flag stored at generation time would be stale.
 *
 * NO DEDICATED ORACLE-PRICE FETCHER, deliberately. Every asset in every live
 * Twyne market — the three PT-srUSDe maturities, wstETH, WETH, USDe, USDC,
 * USDT, WBTC — is ALREADY priced by the Aave and Euler price fetchers, from the
 * very oracles Twyne liquidates against (its Aave markets use Aave's oracle;
 * its Euler markets use the eVault's). A `twyne` fetcher would re-read those
 * same feeds, adding RPC cost and a second source of truth for one number. If
 * Twyne ever whitelists collateral that no integrated protocol prices, THAT is
 * when to add one — reading `oracleRouter`, which is in the config for it.
 */

/**
 * Aave V3 `Pool` — the eMode shape only.
 *
 * `getReserveData` deliberately comes from the shared `AaveV2V3Abi` instead of
 * a hand-written tuple: the struct has gained and renamed fields across V3
 * revisions, and a mismatched local copy does not throw — viem simply fails to
 * decode and the rate silently reads 0%. That is exactly how the first version
 * of this fetcher reported 0% supply and 0% borrow on every Aave market.
 */
const AAVE_EMODE_ABI = [
  {
    type: 'function',
    name: 'getEModeCategoryCollateralConfig',
    stateMutability: 'view',
    inputs: [{ type: 'uint8' }],
    outputs: [
      {
        type: 'tuple',
        components: [
          { name: 'ltv', type: 'uint16' },
          { name: 'liquidationThreshold', type: 'uint16' },
          { name: 'liquidationBonus', type: 'uint16' },
        ],
      },
    ],
  },
] as const

/** EVK reads shared by the credit vault and (on the Euler side) the target. */
const EVK_ABI = [
  { type: 'function', name: 'totalAssets', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'totalBorrows', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'cash', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'interestRate', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
  { type: 'function', name: 'interestFee', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint16' }] },
  { type: 'function', name: 'decimals', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint8' }] },
  { type: 'function', name: 'symbol', stateMutability: 'view', inputs: [], outputs: [{ type: 'string' }] },
  { type: 'function', name: 'interestRateModel', stateMutability: 'view', inputs: [], outputs: [{ type: 'address' }] },
  {
    type: 'function',
    name: 'maxDeposit',
    stateMutability: 'view',
    inputs: [{ type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'LTVLiquidation',
    stateMutability: 'view',
    inputs: [{ type: 'address' }],
    outputs: [{ type: 'uint16' }],
  },
] as const

const ERC20_ABI = [
  { type: 'function', name: 'decimals', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint8' }] },
  {
    type: 'function',
    name: 'balanceOf',
    stateMutability: 'view',
    inputs: [{ type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
] as const

const RAY = 1e27
/** A failed multicall entry arrives as `undefined` OR the empty word `0x`. */
const ok = (v: any) => v !== undefined && v !== null && v !== '0x'
const big = (v: any): bigint | undefined => {
  if (typeof v === 'bigint') return v
  if (typeof v === 'number') return BigInt(v)
  if (typeof v === 'string' && /^(0x[0-9a-fA-F]+|\d+)$/.test(v)) return BigInt(v)
  return undefined
}

export async function fetchTwyneMarkets(
  lender: string,
  chainId: string,
): Promise<TwyneMarketsRaw | undefined> {
  const config = twyneConfigFor(lender, chainId)
  const markets = twyneChainData(lender, chainId)?.markets ?? []
  if (!config?.collateralVaultFactory || markets.length === 0) return undefined

  const nowSec = Math.floor(Date.now() / 1000)

  // --- round 1: credit vaults, LTV bounds, wrapper scale, pause -----------
  const perMarket = markets.flatMap((m) => [
    { address: m.intermediateVault, name: 'totalAssets', args: [] },
    { address: m.intermediateVault, name: 'totalBorrows', args: [] },
    { address: m.intermediateVault, name: 'cash', args: [] },
    { address: m.intermediateVault, name: 'interestRate', args: [] },
    { address: m.intermediateVault, name: 'decimals', args: [] },
    { address: m.intermediateVault, name: 'symbol', args: [] },
    { address: m.intermediateVault, name: 'interestRateModel', args: [] },
    // Probed with a neutral address: EVK `maxDeposit` is cap headroom, not a
    // permission, and there is no per-account gate on these vaults.
    { address: m.intermediateVault, name: 'maxDeposit', args: ['0x000000000000000000000000000000000000dEaD'] },
  ])
  const creditReads = (await multicallRetryUniversal({
    chain: chainId,
    calls: perMarket,
    abi: EVK_ABI,
    allowFailure: true,
  })) as any[]

  const bounds = (await multicallRetryUniversal({
    chain: chainId,
    calls: markets.flatMap((m) => [
      { address: config.vaultManager, name: 'maxTwyneLTVs', args: [m.intermediateVault] },
      { address: config.vaultManager, name: 'externalLiqBuffers', args: [m.intermediateVault] },
    ]),
    abi: TwyneVaultManagerAbi,
    allowFailure: true,
  })) as any[]

  const [pausedRaw] = (await multicallRetryUniversal({
    chain: chainId,
    calls: [{ address: config.collateralVaultFactory, name: 'paused', args: [] }],
    abi: TwyneCollateralVaultFactoryAbi,
    allowFailure: true,
  })) as any[]
  const paused = Boolean(pausedRaw)

  // --- round 2: the external protocol, per vault type ---------------------
  const aaveMarkets = markets.filter((m) => m.vaultType === 'AAVE_V3')
  const eulerMarkets = markets.filter((m) => m.vaultType === 'EULER_V2')

  // categoryId 0 would mean NO eMode, which no live Twyne market uses. The read
  // still runs so a mismatch surfaces as a MISSING threshold (the converter then
  // skips the market) rather than as a wrong one.
  const aaveEmode = aaveMarkets.length
    ? ((await multicallRetryUniversal({
        chain: chainId,
        calls: aaveMarkets.map((m) => ({
          address: m.targetVault,
          name: 'getEModeCategoryCollateralConfig',
          args: [m.categoryId ?? 0],
        })),
        abi: AAVE_EMODE_ABI,
        allowFailure: true,
      })) as any[])
    : []
  const aaveReserves = aaveMarkets.length
    ? ((await multicallRetryUniversal({
        chain: chainId,
        calls: aaveMarkets.flatMap((m) => [
          { address: m.targetVault, name: 'getReserveData', args: [m.underlyingAsset] },
          { address: m.targetVault, name: 'getReserveData', args: [m.targetAsset] },
        ]),
        abi: AaveV2V3Abi,
        allowFailure: true,
      })) as any[])
    : []

  const eulerReads = eulerMarkets.length
    ? ((await multicallRetryUniversal({
        chain: chainId,
        calls: eulerMarkets.flatMap((m) => [
          { address: m.targetVault, name: 'LTVLiquidation', args: [m.collateralAsset] },
          { address: m.targetVault, name: 'interestRate', args: [] },
          { address: m.targetVault, name: 'cash', args: [] },
          // The collateral is an eToken, i.e. ANOTHER EVault — its supply rate
          // is what the collateral earns underneath, and it lives on a different
          // contract than the debt vault.
          { address: m.collateralAsset, name: 'interestRate', args: [] },
          { address: m.collateralAsset, name: 'totalAssets', args: [] },
          { address: m.collateralAsset, name: 'totalBorrows', args: [] },
          { address: m.collateralAsset, name: 'interestFee', args: [] },
        ]),
        abi: EVK_ABI,
        allowFailure: true,
      })) as any[])
    : []

  // --- round 3: token scale + wrapper conversion + external liquidity -----
  const scaleReads = (await multicallRetryUniversal({
    chain: chainId,
    calls: markets.flatMap((m) => [
      { address: m.underlyingAsset, name: 'decimals', args: [] },
      { address: m.targetAsset, name: 'decimals', args: [] },
      // Aave only: what the pool can actually lend of the target asset.
      { address: m.targetAsset, name: 'balanceOf', args: [m.aToken ?? m.targetVault] },
    ]),
    abi: ERC20_ABI,
    allowFailure: true,
  })) as any[]

  const wrapperReads = (await multicallRetryUniversal({
    chain: chainId,
    calls: markets.map((m) => ({
      address: m.collateralAsset,
      name: 'convertToAssets',
      args: [10n ** 18n],
    })),
    abi: TwyneATokenWrapperAbi,
    allowFailure: true,
  })) as any[]

  const out: TwyneMarketRaw[] = []
  // Aave pools that predate `virtualUnderlyingBalance` need one follow-up read
  // of the DEBT reserve's aToken balance — the holder is only known after
  // `getReserveData`, so it cannot ride the batch above.
  const pendingDebtBalances: { index: number; token: string; holder: string }[] = []
  markets.forEach((m, i) => {
    const c = i * 8
    const creditTotalAssets = big(creditReads[c])
    const creditTotalBorrows = big(creditReads[c + 1])
    const creditCash = big(creditReads[c + 2])
    const creditRateSpy = big(creditReads[c + 3])
    if (creditTotalAssets === undefined || creditCash === undefined || creditRateSpy === undefined) return

    const aaveIdx = aaveMarkets.indexOf(m)
    const eulerIdx = eulerMarkets.indexOf(m)

    let externalLiqLtv: bigint | undefined
    let externalSupplyRate: number | undefined
    let externalBorrowRate: number | undefined
    let externalBorrowLiquidity: bigint | undefined
    // Fallback only: the aToken of the DEBT reserve, when the pool predates
    // `virtualUnderlyingBalance`. Never the collateral's aToken.
    let debtAToken: string | undefined

    if (aaveIdx >= 0) {
      const emode = aaveEmode[aaveIdx]
      const collReserve = aaveReserves[aaveIdx * 2]
      const debtReserve = aaveReserves[aaveIdx * 2 + 1]
      // eMode is the source of truth here — see the header. When it is missing
      // the threshold stays undefined and the converter refuses to publish an
      // LTV band rather than inventing one from the reserve config.
      if (ok(emode) && emode?.liquidationThreshold) externalLiqLtv = BigInt(emode.liquidationThreshold)
      // Aave rates are RAY per YEAR already — no annualisation.
      if (ok(collReserve) && collReserve?.currentLiquidityRate !== undefined)
        externalSupplyRate = Number(collReserve.currentLiquidityRate) / RAY
      if (ok(debtReserve) && debtReserve?.currentVariableBorrowRate !== undefined)
        externalBorrowRate = Number(debtReserve.currentVariableBorrowRate) / RAY
      // AVAILABLE LIQUIDITY comes from the DEBT reserve, and it has to be read
      // here rather than guessed from a balance: `virtualUnderlyingBalance` is
      // the accounting balance Aave itself sizes a borrow against, so it is the
      // number that decides whether a borrow succeeds.
      //
      // The first version of this read used `targetAsset.balanceOf(m.aToken)`,
      // and `m.aToken` is the COLLATERAL's aToken — so it asked how much USDe
      // sits in the PT-srUSDe aToken, which is zero, and every Twyne debt row
      // rendered $0 liquidity while Aave had hundreds of millions available.
      // A balance read against the wrong contract does not fail; it answers.
      if (ok(debtReserve)) {
        const virtual = big(debtReserve.virtualUnderlyingBalance)
        if (virtual !== undefined && virtual > 0n) externalBorrowLiquidity = virtual
        else if (typeof debtReserve.aTokenAddress === 'string')
          debtAToken = debtReserve.aTokenAddress
      }
    } else if (eulerIdx >= 0) {
      const e = eulerIdx * 7
      externalLiqLtv = big(eulerReads[e])
      const spy = big(eulerReads[e + 1])
      // EVK rates are SPY (1e27). Annualise nominally — the package convention.
      if (spy !== undefined) externalBorrowRate = (Number(spy) / RAY) * 31_536_000
      externalBorrowLiquidity = big(eulerReads[e + 2])
      // Collateral eVault: supply APR = borrow x utilization x (1 - fee).
      const collSpy = big(eulerReads[e + 3])
      const collAssets = big(eulerReads[e + 4])
      const collBorrows = big(eulerReads[e + 5])
      const collFeeBps = big(eulerReads[e + 6]) ?? 0n
      if (collSpy !== undefined && collAssets && collAssets > 0n) {
        const util = Number(collBorrows ?? 0n) / Number(collAssets)
        externalSupplyRate =
          (Number(collSpy) / RAY) * 31_536_000 * util * (1 - Number(collFeeBps) / 10_000)
      }
    }

    const s = i * 3
    // `scaleReads[s + 2]` probes `targetAsset.balanceOf(<aToken | targetVault>)`
    // with the address known at BATCH time — which is only right for Euler,
    // where the target vault holds its own cash. On Aave the correct holder is
    // the DEBT reserve's aToken, which is only known after `getReserveData`
    // above, so it is picked up in the follow-up round below.
    if (externalBorrowLiquidity === undefined && !debtAToken) {
      externalBorrowLiquidity = big(scaleReads[s + 2])
    }
    if (debtAToken) pendingDebtBalances.push({ index: out.length, token: m.targetAsset, holder: debtAToken })

    out.push({
      market: m,
      creditTotalAssets,
      creditTotalBorrows: creditTotalBorrows ?? 0n,
      creditCash,
      creditRateSpy,
      creditDepositCapacity: big(creditReads[c + 7]),
      creditDecimals: Number(creditReads[c + 4] ?? 18),
      creditSymbol: typeof creditReads[c + 5] === 'string' ? creditReads[c + 5] : undefined,
      creditIrm: typeof creditReads[c + 6] === 'string' ? creditReads[c + 6].toLowerCase() : m.creditIrm,
      externalLiqLtv,
      maxTwyneLiqLtv: big(bounds[i * 2]) ?? (m.maxTwyneLiqLTV !== undefined ? BigInt(m.maxTwyneLiqLTV) : undefined),
      externalLiqBuffer:
        big(bounds[i * 2 + 1]) ?? (m.externalLiqBuffer !== undefined ? BigInt(m.externalLiqBuffer) : undefined),
      externalSupplyRate,
      externalBorrowRate,
      externalBorrowLiquidity,
      collateralDecimals: Number(scaleReads[s] ?? 18),
      targetDecimals: Number(scaleReads[s + 1] ?? 18),
      collateralToUnderlying: big(wrapperReads[i]),
      // Computed HERE, never read from the roster: a stored flag goes stale,
      // and two of the three live PT markets have already matured.
      collateralMatured: !!m.collateralMaturity && m.collateralMaturity <= nowSec,
      paused,
    })
  })

  if (pendingDebtBalances.length > 0) {
    const balances = (await multicallRetryUniversal({
      chain: chainId,
      calls: pendingDebtBalances.map((p) => ({
        address: p.token,
        name: 'balanceOf',
        args: [p.holder],
      })),
      abi: ERC20_ABI,
      allowFailure: true,
    })) as any[]
    pendingDebtBalances.forEach((p, i) => {
      const v = big(balances[i])
      if (v !== undefined && out[p.index]) out[p.index].externalBorrowLiquidity = v
    })
  }

  if (out.length === 0) return undefined
  return { lender, chainId, config, markets: out }
}
